// featuredTours.ts
// =================
//
// Wave 14ca-fix48: Rider-seitiger Client fuer admin-kuratierte Featured Tours.

import { saveCustomTrail } from './customTrails';
//
// Architektur-Entscheidung (kontrastiert zu runs.ts):
//   - KEIN IDB-Cache in v1. Featured Tours sind public und Liste ist klein
//     (~kB), Refresh bei jedem List-Page-Open ist tragbar.
//   - Cross-User-Anzahl bleibt klein (admin-kuratiert), keine
//     Concurrency-Pool-Komplexitaet noetig.
//   - GPX wird bei Map-Open lazy gefetched, nicht beim Listing.
//
// Kann spaeter zur Cache-Strategie analog runs.ts erweitert werden wenn der
// Anwendungsfall waechst.
//
// Pseudo-ID-Konvention auf der Map: 'featured:<id>' — analog 'run:' und
// 'custom:'. MapPage-Komponenten die bei Pseudo-IDs aussteigen muessen
// (AddCruxHere, RunsForTrail, etc.) erkennen das ueber isFeaturedTourPseudoId.

export interface FeaturedTour {
  id: string;
  name: string;
  trailIds: string[];
  gpxFilename: string;
  description?: string | null;
  difficulty: string;
  sizeBytes?: number;
  updatedAt: string;
}

/** Public-Endpoint (kein Auth noetig). */
export async function listFeaturedTours(): Promise<FeaturedTour[]> {
  try {
    const res = await fetch('/api/featured-tours', {
      credentials: 'include',
    });
    if (!res.ok) {
      console.warn('[featured-tours] list', res.status);
      return [];
    }
    const body = (await res.json()) as { tours?: FeaturedTour[] };
    return Array.isArray(body.tours) ? body.tours : [];
  } catch (err) {
    console.warn('[featured-tours] list failed', err);
    return [];
  }
}

/** Liefert die GPX-Datei fuer eine Tour. Caller parsed sie selbst (siehe
    parseGpx in @adtu/shared). */
export async function fetchFeaturedTourGpx(
  filename: string,
): Promise<string | null> {
  try {
    const res = await fetch(
      `/api/featured-tours/${encodeURIComponent(filename)}`,
      { credentials: 'include' },
    );
    if (!res.ok) {
      console.warn('[featured-tours] gpx', res.status, filename);
      return null;
    }
    return await res.text();
  } catch (err) {
    console.warn('[featured-tours] gpx fetch failed', err);
    return null;
  }
}

/**
 * Wave 14cd: Admin-only delete via DELETE /api/admin/featured-tours/:id.
 *
 * Backend gated by Admin-Token; nicht-Admin-Sessions kriegen 401/403.
 * Wir geben true bei success zurück, false sonst (Caller zeigt UI-Toast).
 * Auth-Cookie wird via credentials:'include' mitgeschickt.
 */
export async function deleteFeaturedTour(id: string): Promise<boolean> {
  try {
    const res = await fetch(
      `/api/admin/featured-tours/${encodeURIComponent(id)}`,
      { method: 'DELETE', credentials: 'include' },
    );
    if (!res.ok) {
      console.warn('[featured-tours] delete failed', res.status, id);
      return false;
    }
    return true;
  } catch (err) {
    console.warn('[featured-tours] delete threw', err);
    return false;
  }
}

/**
 * Wave 14cd: Reverse-Feature für Featured Tours.
 *
 * Im Gegensatz zu Custom Tours können wir die Featured Tour selbst nicht
 * umdrehen — sie ist server-owned und shared. Stattdessen erstellen wir
 * eine REVERSE-Kopie als CUSTOM TOUR im lokalen IDB des Users. Damit hat
 * der User seine private umgekehrte Version, die Original-Featured-Tour
 * bleibt unangetastet.
 *
 * Ablauf:
 *   1. GPX vom Server holen.
 *   2. Parsen → Punkte-Array.
 *   3. Coords reversen (lon/lat/ele-Tuple bleibt erhalten).
 *   4. saveCustomTrail() mit Name "<TourName> (reversed)".
 *
 * Returnt die neue Custom-Tour-ID oder null bei Fehler.
 */
export async function reverseFeaturedTourToCustomTour(
  tour: FeaturedTour,
  domParser: DOMParser,
): Promise<string | null> {
  const gpx = await fetchFeaturedTourGpx(tour.gpxFilename);
  if (!gpx) return null;
  let tracks;
  try {
    tracks = parseGpx(gpx, { domParser });
  } catch (err) {
    console.warn('[featured-tours] reverse parse failed', tour.id, err);
    return null;
  }
  if (!tracks || tracks.length === 0) return null;
  const allPoints = tracks.flatMap((t) => t.points);
  if (allPoints.length < 2) return null;
  // Coords mit optionaler Elevation als Tupel — Reverse einfach durch
  // .reverse(). Reihenfolge der Tupel-Komponenten bleibt erhalten.
  const reversedCoords: Array<[number, number] | [number, number, number]> =
    allPoints
      .slice()
      .reverse()
      .map((p) =>
        p.ele != null && Number.isFinite(p.ele)
          ? ([p.lon, p.lat, p.ele] as [number, number, number])
          : ([p.lon, p.lat] as [number, number]),
      );
  // lengthM ist die Geometrie-Länge — bleibt durch Reverse identisch zur
  // Original-Tour. Wir nutzen den Server-stat aus FeaturedTour.sizeBytes
  // NICHT (das ist nur die GPX-File-Grösse), sondern rechnen kurz selber.
  const lengthM = computeLengthM(allPoints);
  const saved = await saveCustomTrail({
    name: `${tour.name} (reversed)`,
    coords: reversedCoords,
    lengthM,
    trailsUsed: tour.trailIds.slice().reverse(),
    difficulty: tour.difficulty,
  });
  return saved.id;
}

/** Track-Länge via Haversine zwischen aufeinanderfolgenden Punkten.
 *  Kleine Helper-Funktion lokal um trailGeom-Dep zu vermeiden. */
function computeLengthM(
  points: Array<{ lat: number; lon: number }>,
): number {
  if (points.length < 2) return 0;
  const R = 6371000;
  const toRad = (d: number) => (d * Math.PI) / 180;
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1]!;
    const b = points[i]!;
    const dLat = toRad(b.lat - a.lat);
    const dLon = toRad(b.lon - a.lon);
    const x =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) ** 2;
    total += 2 * R * Math.asin(Math.sqrt(x));
  }
  return total;
}

export const FEATURED_TOUR_ID_PREFIX = 'featured:';

export function isFeaturedTourPseudoId(
  id: string | null | undefined,
): boolean {
  return typeof id === 'string' && id.startsWith(FEATURED_TOUR_ID_PREFIX);
}

export function extractFeaturedTourId(pseudoId: string): string {
  if (!isFeaturedTourPseudoId(pseudoId)) return '';
  return pseudoId.slice(FEATURED_TOUR_ID_PREFIX.length);
}

// ─────────────────────────────────────────────────────────────────────────────
// Map-Wrap: Featured Tour als CachedTrail
// ─────────────────────────────────────────────────────────────────────────────

import {
  parseGpx,
  simplifyTrack,
  trackLength,
  elevationStats,
  type Difficulty,
} from '@adtu/shared';
import type { CachedTrail } from './trails';

/** Holt GPX, parsed, und baut eine CachedTrail-Hülle die MapPage analog zu
    runAsTrail / customTrailAsTrail behandeln kann. Pseudo-ID-Format
    'featured:<id>' damit die MapPage Pseudo-ID-Checks (AddCruxHere etc.)
    konsistent ausschalten kann. Returns null bei Fetch-/Parse-Fehler. */
export async function featuredTourAsTrail(
  tour: FeaturedTour,
  domParser: DOMParser,
): Promise<CachedTrail | null> {
  const gpx = await fetchFeaturedTourGpx(tour.gpxFilename);
  if (!gpx) return null;
  let tracks;
  try {
    tracks = parseGpx(gpx, { domParser });
  } catch (err) {
    console.warn('[featured-tours] parse failed', tour.id, err);
    return null;
  }
  if (!tracks || tracks.length === 0) return null;
  // Bei Multi-Track-GPX (selten, aber GPX erlaubt's) konkatenieren wir die
  // Punkte — die Tour ist konzeptionell EIN Track. simplifyTrack reduziert
  // dann die Punkte auf Map-Detail-Niveau.
  const allPoints = tracks.flatMap((t) => t.points);
  if (allPoints.length < 2) return null;
  const simplifiedPoints = simplifyTrack(allPoints);
  const track = { name: tour.name, points: simplifiedPoints };
  const stats = {
    lengthMeters: trackLength(track.points),
    ...elevationStats(track.points, { deadbandMeters: 2 }),
  };
  return {
    id: `${FEATURED_TOUR_ID_PREFIX}${tour.id}`,
    name: tour.name,
    difficulty: (tour.difficulty as Difficulty) || 'unknown',
    // Eigenes Label fuers Detail-Sheet, keine Logik-Sonderung anderswo.
    trailType: 'Featured Tour',
    colorHex: null,
    description: tour.description ?? null,
    photos: [],
    gpxFilename: tour.gpxFilename,
    track,
    // Featured Tours haben keine eigenen Cruxes — koennen aber spaeter aus
    // den enthaltenen Trails aggregiert werden (analog runs.aggregateCruxes).
    cruxPoints: [],
    stats,
    cachedAt: new Date().toISOString(),
    // Wave 14cl: tour.trailIds als Allow-List fuer ElevationProfile-
    // Segment-Faerbung mitschleppen. MapPage berechnet die routeSegments
    // selbst aus der GPX-Geometrie (Spatial-Snap, deriveRouteSegments) —
    // wir geben hier nur die Whitelist mit damit zufaellig-nahe Trails
    // nicht eingefaerbt werden, sondern nur die admin-kuratierte Liste.
    selectedTrailIds: tour.trailIds && tour.trailIds.length > 0 ? tour.trailIds : undefined,
  };
}
